import { useEffect } from 'react';

import useSelectPlayerModalAdapter from 'application/components/common/SelectPlayerFlow/SelectPlayerModal/useSelectPlayerModalViewModel';
import {
  usePointRepositoryInjector,
  useBillingRepositoryInjector,
  useAccountRepositoryInjector,
  usePaymentMethodRepositoryInjector,
  useStreamRepositoryInjector,
} from 'application/providers/DIContainerProvider';
import {
  NOTIFICATION_DEFAULT_ERROR_MESSAGE,
  NotificationContextType,
} from 'application/providers/NotificationProvider';
import { TEXTS } from 'application/pages/texts';
import { TEXTS as POINT_TEXTS } from 'application/pages/PointsPage/renderer/texts';
import LicensesUtils from 'application/repositories/billing/utils';
import useAddressState from 'application/hooks/useAddressState';
import ValidationUtils from 'application/utils/ValidationUtils';
import AnalyticsService, {
  ParamNameAMP,
  PointEventNameAMP,
  StreamBindFlowEventNameAMP,
  StreamBindFlowParamNameAMP,
} from 'application/services/AnalyticsService';
import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';
import { selectIsAcceptanceAdvancedPolling, startAcceptanceAdvancedSigning } from 'application/store';
import {
  CurrentPointOnboardingType,
  CurrentStreamOnboardingType,
  SelectPlayerFlowType,
} from 'application/components/common';
import { RouterUtils } from 'application/providers/RouterProvider';
import { INTEGRATION_PAGE_PATH } from 'application/consts';
import { useAccountCompanyId, useAccountSelectors } from 'application/hooks';
import { useAppDispatch, useAppSelector } from 'application/providers/StoreProvider';

import {
  AcceptanceAdvanceStatus,
  PaymentKind,
  PaymentScheme,
  SubscriptionStatus,
  UserFeatures,
} from 'domain/api/graphql/generated';
import useAcceptanceAdvanced from 'domain/billing/useAcceptanceAdvanced';

import DateUtils from '@zvuk-b2b/react-uikit/toolkit/DateUtils';

import { PointCreateModalPages, UsePointCreateModalViewModel } from './types';
import { getPointCreateMutationInput, sendEventsAfterPointCreating } from './utils';

interface Props {
  flowType?: SelectPlayerFlowType;
  isTemplateMode?: boolean;
  targetStreamId?: string;
  collectionId?: string;
  page: PointCreateModalPages;
  notification: NotificationContextType;
  setCurrentPoint: (currentPoint: CurrentPointOnboardingType) => void;
  setCurrentPointStream: (stream: CurrentStreamOnboardingType) => void;
  setCurrentPointIsEnabled: (isEnabled: boolean) => void;
  redirectToPointDrawer: (pointId: string) => void;
  replaceModal: <T extends ModalTypes>(
    type: T,
    modalState: Omit<ModalProps[T], 'open' | 'close' | 'openModal' | 'replaceModal'>,
    onModalCloseCallback?: () => void
  ) => void;
  openModal: ((type: ModalTypes, props: {}) => void) &
    (<T extends ModalTypes>(
      type: T,
      modalState: Omit<ModalProps[T], 'close' | 'open' | 'openModal' | 'replaceModal'>,
      onModalCloseCallback?: () => void
    ) => void);
}

const URI_FOR_RETURN = `${window.location.origin}/${RouterUtils.generatePath(INTEGRATION_PAGE_PATH, { type: 'sbbol' })}`;

const usePointCreateViewModel = ({
  flowType,
  isTemplateMode,
  targetStreamId,
  collectionId,
  page,
  notification,
  setCurrentPoint,
  setCurrentPointStream,
  setCurrentPointIsEnabled,
  redirectToPointDrawer,
  replaceModal,
  openModal,
}: Props): UsePointCreateModalViewModel => {
  const dispatch = useAppDispatch();
  const isPolling = useAppSelector(selectIsAcceptanceAdvancedPolling());

  const selectors = useAccountSelectors();

  const companyId = useAccountCompanyId();
  const companyCountry = useAppSelector(selectors.selectCompanyCountry);
  const companyInvoices = useAppSelector(selectors.selectInvoices);

  const { userOnboardingFeaturesLoading } = useAccountRepositoryInjector();
  const { getPaymentMethod, readPaymentMethod } = usePaymentMethodRepositoryInjector();

  const { paymentMethod } = readPaymentMethod(companyId);

  const { createPoint, createPointLoading, createPointError, getStandardizedPoints, getStandardizedPointsLoading } =
    usePointRepositoryInjector();

  const {
    getExtendSubscriptionCost,
    subscriptionCostResult,
    extendOneShotSubscription,
    isExtendSubscriptionLoading,
    getBillingInfo,
    watchBillingInfo,
    licenseCostLoading,
  } = useBillingRepositoryInjector();

  const {
    isBillingEnabled,
    hasTrialLicenses,
    hasActiveLicenses,
    selectedPaymentKind,
    paymentScheme,
    status,
    renewableLicenses,
    nextBillingDate,
  } = watchBillingInfo(companyId);

  const {
    changeStream,
    isChangeStreamLoading,
    copyTemplateStream,
    isCopyStreamTemplateLoading,
    onboardingPassedRequest,
    isUserFeaturesOnboardingPassLoading,
    getSavedStreams,
    isSavedStreamsLoading,
  } = useSelectPlayerModalAdapter();

  const { readStreamWithThematic, clearStreamWithThematic } = useStreamRepositoryInjector();

  const { acceptanceAdvanced, loading: acceptanceAdvancedLoading } = useAcceptanceAdvanced(companyId);

  const addressState = useAddressState({ location: companyCountry });

  const isButtonLoading =
    getStandardizedPointsLoading ||
    createPointLoading ||
    isExtendSubscriptionLoading ||
    isChangeStreamLoading ||
    isCopyStreamTemplateLoading ||
    isUserFeaturesOnboardingPassLoading ||
    isSavedStreamsLoading ||
    acceptanceAdvancedLoading;

  const isButtonWithCostLoading =
    isButtonLoading || licenseCostLoading || subscriptionCostResult.data.extendCost === undefined;

  const isAcceptanceAdvancedNull =
    selectedPaymentKind === PaymentKind.Sbbol &&
    (acceptanceAdvanced === null || acceptanceAdvanced?.status === null) &&
    !hasTrialLicenses;

  const isAcceptanceAdvancedPending =
    selectedPaymentKind === PaymentKind.Sbbol &&
    acceptanceAdvanced?.status === AcceptanceAdvanceStatus.Pending &&
    !hasTrialLicenses;

  const needShowAcceptanceAdvancedModal =
    selectedPaymentKind === PaymentKind.Sbbol && isAcceptanceAdvancedNull && !hasTrialLicenses;

  const isLastInvoiceHasZeroAmount = LicensesUtils.getLastPaidPeriodicInvoice(companyInvoices || [])?.amount === 0;

  const isAcceptanceAdvancedAuthorizedOrActive =
    acceptanceAdvanced?.status === AcceptanceAdvanceStatus.Authorized ||
    acceptanceAdvanced?.status === AcceptanceAdvanceStatus.Active;

  const canPay = selectedPaymentKind === PaymentKind.Sbbol ? isAcceptanceAdvancedAuthorizedOrActive : paymentMethod;

  const canAddPointWithPayment =
    Boolean(isBillingEnabled) && canPay && !isLastInvoiceHasZeroAmount && hasActiveLicenses;

  useEffect(() => {
    switch (acceptanceAdvanced?.status) {
      case AcceptanceAdvanceStatus.Canceled: {
        notification.showInfoNotification({
          children: TEXTS.POINT_CREATE_ACCEPTANCE_ADVANCED_CANCELLED,
          delay: 10000,
        });

        break;
      }
      default: {
        break;
      }
    }
  }, [acceptanceAdvanced?.status, isPolling]);

  const initData = async () => {
    if (companyId) {
      await getBillingInfo(companyId);
      await getPaymentMethod(companyId);
    }

    if (isBillingEnabled) {
      await getExtendSubscriptionCost(companyId, 1);
    }
  };

  useEffect(() => {
    initData();
  }, []);

  const changeStreamOnNewPoint = async (pointId: string) => {
    let streamId = targetStreamId;
    const withThematic = readStreamWithThematic();

    if (isTemplateMode && streamId) {
      streamId = await copyTemplateStream(streamId, companyId, withThematic);
    }

    if (streamId) {
      changeStream([
        {
          newStreamId: streamId,
          pointId,
        },
      ]);

      getSavedStreams([companyId]);
      onboardingPassedRequest({ feature: UserFeatures.WarningStreamFirstStart });

      AnalyticsService.event(StreamBindFlowEventNameAMP.STREAM_BOUND, {
        [ParamNameAMP.STREAM_ID]: streamId,
        [ParamNameAMP.COLLECTION_ID]: collectionId,
      });

      setCurrentPointStream({ id: streamId });
      clearStreamWithThematic();
    }
  };

  const onCreatePointModalNext = async () => {
    AnalyticsService.event(StreamBindFlowEventNameAMP.STREAM_RUN_AT_CREATED_POINT_CLICKED, {
      [StreamBindFlowParamNameAMP.FLOW_TYPE]: flowType,
    });

    AnalyticsService.event(PointEventNameAMP.POINT_CREATE_MODAL_SUCCESS_SUBMIT, {
      [StreamBindFlowParamNameAMP.FLOW_TYPE]: flowType,
    });

    replaceModal(ModalTypes.SELECT_PLAYER_MODAL, {});
  };

  const handleSuccessCreatePointWithPayment = async (pointId: string, licenseId?: string) => {
    try {
      if (canAddPointWithPayment && licenseId) {
        await extendOneShotSubscription(companyId, [licenseId]);

        notification.showInfoNotification({
          children: POINT_TEXTS.POINT_CREATE_CONFIRM_MODAL_WITH_PAYMENT_SUCCESS_TEXT,
        });

        setCurrentPointIsEnabled(true);
      }
    } catch (e) {
      notification.showErrorNotification({
        children: NOTIFICATION_DEFAULT_ERROR_MESSAGE,
      });
    } finally {
      if (page === PointCreateModalPages.STREAM) {
        await changeStreamOnNewPoint(pointId);
      } else {
        redirectToPointDrawer(pointId);
      }

      onCreatePointModalNext();
    }
  };

  const handleCreateClick = async () => {
    const input = await getPointCreateMutationInput(companyId, addressState.value, getStandardizedPoints);
    const result = await createPoint(input);

    if (result.ok && result.point) {
      sendEventsAfterPointCreating(result.point.id, result.point.isEnabled, page);
      setCurrentPoint(result.point);
      await handleSuccessCreatePointWithPayment(result.point.id, result.point.license?.id);
    } else {
      notification.showErrorNotification({
        children: NOTIFICATION_DEFAULT_ERROR_MESSAGE,
      });
    }
  };

  const onPointCreateConfirmModalShow = () => {
    openModal(ModalTypes.POINT_CREATE_CONFIRM, {
      onSubmit: async () => {
        await handleCreateClick();
      },
      cost: subscriptionCostResult.data.extendCost?.cost,
    });
  };

  const openAcceptanceAdvancedSigning = () => {
    dispatch(
      startAcceptanceAdvancedSigning({
        companyId,
        uriForReturn: URI_FOR_RETURN,
        fallbackFn: () => {
          openModal(ModalTypes.SBERBUSSINESS_OPEN, {});
        },
      })
    );
  };

  const startDate = new Date();
  const endDate = nextBillingDate
    ? DateUtils.subDays(new Date(nextBillingDate), 1)
    : DateUtils.getLastDayOfMonth(startDate);

  const formatStartNextBillingDate = DateUtils.getFormatDateFrom(startDate, 'dd.MM');
  const formatEndNextBillingDate = DateUtils.getFormatDateFrom(endDate, 'dd.MM');

  return {
    addressState,
    extendCost: subscriptionCostResult.data.extendCost?.cost,
    extendFullPeriodCost:
      (subscriptionCostResult.data.extendCost?.fullPeriodCost || 0) * (renewableLicenses?.length || 0 + 1),
    createPointError,
    userOnboardingFeaturesLoading,
    isButtonDisabled:
      !ValidationUtils.validateAddress(addressState.value) || isAcceptanceAdvancedPending || isAcceptanceAdvancedNull,
    isButtonLoading: canAddPointWithPayment ? isButtonWithCostLoading : isButtonLoading,
    isShowHintAboutPrice: Boolean(isBillingEnabled) && !hasTrialLicenses,
    needShowAcceptanceAdvancedModal,
    isAcceptanceAdvancedNull:
      isAcceptanceAdvancedNull || (selectedPaymentKind === PaymentKind.Sbbol && acceptanceAdvancedLoading),
    isAcceptanceAdvancedPending,
    canAddPointWithPayment,
    withResidualPayment:
      paymentScheme === PaymentScheme.InvoiceCalendarMonthly && status !== SubscriptionStatus.InactiveSubscription,
    formatNextBillingDate: `${formatStartNextBillingDate} - ${formatEndNextBillingDate}`,
    onCreatePointButtonClick: canAddPointWithPayment ? onPointCreateConfirmModalShow : handleCreateClick,
    openAcceptanceAdvancedSigning,
  };
};

export default usePointCreateViewModel;
